import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { useCart } from '../../context/CartContext';
import { db } from '../../firebase';
import { API_BASE_URL } from '../../constants';
import { auth } from '../../firebase';
import './PurchasedLibraryPage.css';

function PurchasedLibraryPage() {
  const navigate = useNavigate();
  const { purchases, currentUser } = useCart();
  const [enriched, setEnriched] = useState([]);

  // Enrich purchases with full project data (image, fileNames, etc.)
  useEffect(() => {
    if (!purchases.length) { setEnriched([]); return; }

    let cancelled = false;

    async function enrich() {
      const results = await Promise.all(
        purchases.map(async (p) => {
          try {
            // projectId is a custom field, NOT the Firestore doc ID
            // so we must query by field value
            const q = query(
              collection(db, 'marketplace-projects'),
              where('projectId', '==', p.projectId)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              const proj = snap.docs[0].data();
              return {
                ...p,
                imageUrl: p.imageUrl || proj.imageUrl || proj.imageUrls?.[0] || null,
                folderName: p.folderName || proj.folderName,
                stepFileName: proj.stepFileName || null,
                xmlFileName: proj.xmlFileName || null,
              };
            }
          } catch (e) {
            console.warn('Could not enrich purchase', p.projectId, e);
          }
          return p; // fallback to raw purchase
        })
      );
      if (!cancelled) setEnriched(results);
    }

    enrich();
    return () => { cancelled = true; };
  }, [purchases]);

  const downloadFile = async (purchase, fileType) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const fileName = fileType === 'STEP' ? purchase.stepFileName : purchase.xmlFileName;

      if (!fileName) {
        alert(`${fileType} file not available for this project`);
        return;
      }

      if (!purchase.folderName) {
        alert('Project folder information is missing');
        return;
      }

      const url = `${API_BASE_URL}/api/download-file?folderName=${encodeURIComponent(purchase.folderName)}&fileName=${encodeURIComponent(fileName)}`;

      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  if (!currentUser) {
    return (
      <div className="purchased-library">
        <div className="purchased-library__empty">
          <h2>Please log in to view your purchased projects</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="purchased-library">
      <header className="purchased-library__header">
        <h1>My Purchased Projects</h1>
        <p>Projects you've acquired from the marketplace</p>
      </header>

      {enriched.length === 0 && purchases.length === 0 ? (
        <div className="purchased-library__empty">
          <h2>No purchases yet</h2>
          <p>Browse the marketplace and add projects to your cart!</p>
        </div>
      ) : (
        <div className="purchased-library__grid">
          {enriched.map((purchase) => (
            <div
              key={purchase.id}
              className="purchased-library__card purchased-library__card--clickable"
              onClick={() => navigate(`/marketplace?id=${purchase.projectId}`)}
            >
              <div className="purchased-library__card-image">
                {purchase.imageUrl ? (
                  <img src={purchase.imageUrl} alt={purchase.name} />
                ) : (
                  <div className="purchased-library__card-placeholder">
                    {purchase.name?.charAt(0).toUpperCase() || 'P'}
                  </div>
                )}
              </div>
              <div className="purchased-library__card-info">
                <h3>{purchase.name}</h3>
                <span className="purchased-library__card-date">
                  Purchased: {purchase.purchasedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                </span>
              </div>
              <div className="purchased-library__card-actions" onClick={(e) => e.stopPropagation()}>
                {purchase.stepFileName && (
                  <button
                    className="purchased-library__download-btn"
                    onClick={() => downloadFile(purchase, 'STEP')}
                  >
                    Download STEP
                  </button>
                )}
                {purchase.xmlFileName && (
                  <button
                    className="purchased-library__download-btn"
                    onClick={() => downloadFile(purchase, 'XML')}
                  >
                    Download XML
                  </button>
                )}
                {!purchase.stepFileName && !purchase.xmlFileName && (
                  <span className="purchased-library__no-files">No downloadable files</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PurchasedLibraryPage;
